import { ActorType, AuditPolicy, AuthenticationAssuranceLevel, ConcurrencyPolicy, IdempotencyPolicy, PrivacyClassification, ScopeType, } from './operation.js';
import { FEE_RULE_OPERATIONS, FEE_RULE_OPERATION_IDS, } from './fee-rule.operations.js';
import { ORGANIZATION_REGULATORY_PROFILE_OPERATIONS } from './organization-regulatory-profile.operations.js';
export const ACCOUNTING_GOVERNANCE_READ = 'accounting.governance.read';
export const ORGANIZATION_GOVERNANCE_WORKSPACE_READ = 'organization.governance.workspace.read';
export const ACCOUNTING_GOVERNANCE_RECORD = 'accounting.governance.record';
export const COMPLIANCE_GOVERNANCE_READ = 'compliance.governance.read';
export const COMPLIANCE_GOVERNANCE_RECORD = 'compliance.governance.record';
export const COMPLIANCE_GOVERNANCE_APPROVE = 'compliance.governance.approve';
export const CLIENT_GOVERNANCE_READ = 'clients.governance.read';
export const CLIENT_GOVERNANCE_RECORD = 'clients.governance.record';
export const PRIVACY_RIGHTS_READ = 'privacy.rights.read';
export const PRIVACY_RIGHTS_MANAGE = 'privacy.rights.manage';
export const INTEGRATION_GOVERNANCE_READ = 'integrations.governance.read';
export const INTEGRATION_GOVERNANCE_MANAGE = 'integrations.governance.manage';
export const CONTROL_PLANE_ENTITLEMENTS_SYNCHRONIZE = 'integrations.control-plane.entitlements.synchronize';
export const CONTROL_PLANE_REFERENCE_DATA_SYNCHRONIZE = 'integrations.control-plane.reference-data.synchronize';
export const CONTROL_PLANE_SUPPORT_DELEGATION_SYNCHRONIZE = 'integrations.control-plane.support-delegation.synchronize';
export const MONETARY_APPROVAL_READ = 'monetary-approval.read';
export const MONETARY_APPROVAL_MANAGE = 'monetary-approval.manage';
export const MONETARY_APPROVAL_DECIDE = 'monetary-approval.decide';
export const RISK_ANALYTICS_READ = 'risk.analytics.read';
export const RISK_ANALYTICS_RUN = 'risk.analytics.run';
export const RISK_ANALYTICS_RESOLVE = 'risk.analytics.resolve';
const AREAS = {
    workspace: {
        module: 'ADMIN', requiredModules: ['ADMIN'],
        readPermission: 'organization:governance:read', writePermission: 'organization:governance:read',
        privacy: PrivacyClassification.RESTRICTED,
    },
    accounting: {
        module: 'ACCOUNTING', requiredModules: ['ACCOUNTING'],
        readPermission: 'organization:accounting:read', writePermission: 'organization:accounting:manage',
        privacy: PrivacyClassification.RESTRICTED,
    },
    compliance: {
        module: 'REGULATORY', requiredModules: ['REGULATORY', 'REPORTING'],
        readPermission: 'organization:compliance:read', writePermission: 'organization:compliance:manage',
        privacy: PrivacyClassification.RESTRICTED,
    },
    clients: {
        module: 'ADMIN', requiredModules: ['ADMIN', 'REPORTING'],
        readPermission: 'organization:client-governance:read', writePermission: 'organization:client-governance:manage',
        privacy: PrivacyClassification.CONFIDENTIAL,
    },
    privacy: {
        module: 'ADMIN', requiredModules: ['ADMIN'],
        readPermission: 'organization:privacy:read', writePermission: 'organization:privacy:manage',
        privacy: PrivacyClassification.RESTRICTED,
    },
    integrations: {
        module: 'ADMIN', requiredModules: ['ADMIN'],
        readPermission: 'organization:integrations:read', writePermission: 'organization:integrations:manage',
        privacy: PrivacyClassification.RESTRICTED,
    },
    monetary: {
        module: 'ADMIN', requiredModules: ['ADMIN', 'ACCOUNTING'],
        readPermission: 'organization:monetary-approval:read', writePermission: 'organization:monetary-approval:manage',
        privacy: PrivacyClassification.RESTRICTED,
    },
    risk: {
        module: 'INVESTMENT', requiredModules: ['INVESTMENT', 'REPORTING'],
        readPermission: 'organization:risk:read', writePermission: 'organization:risk:manage',
        privacy: PrivacyClassification.CONFIDENTIAL,
    },
};
function operation(id, name, area, options = {}) {
    const readOnly = options.readOnly ?? false;
    return {
        id,
        name,
        labelKey: (readOnly
            ? 'operations.organizationGovernance.read.label'
            : 'operations.organizationGovernance.manage.label'),
        descriptionKey: (readOnly
            ? 'operations.organizationGovernance.read.description'
            : 'operations.organizationGovernance.manage.description'),
        permission: readOnly ? area.readPermission : area.writePermission,
        permissionId: (readOnly ? area.readPermission : area.writePermission),
        module: area.module,
        requiredModules: [...area.requiredModules],
        requiresEntitledModule: true,
        requiredAal: readOnly ? AuthenticationAssuranceLevel.AAL1 : AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        allowedScopeKinds: ['ORGANIZATION', 'BRANCH'],
        allowedPersonas: ['ORG_ADMIN'],
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        requestedScope: {
            organizationId: [{ source: 'query', field: 'organizationId' }],
            branchId: [{ source: 'query', field: 'branchId' }],
        },
        forcesClientScope: false,
        makerCheckerRequired: options.makerChecker ?? false,
        idempotencyPolicy: readOnly ? IdempotencyPolicy.NOT_APPLICABLE : IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: readOnly ? ConcurrencyPolicy.NONE : ConcurrencyPolicy.OPTIMISTIC,
        auditPolicy: readOnly ? AuditPolicy.READ : AuditPolicy.SENSITIVE,
        privacyClassification: area.privacy,
        telemetryId: id,
        allowedActorTypes: [ActorType.USER],
        httpMethods: readOnly ? ['GET'] : ['POST', 'PATCH'],
        isReadOnly: readOnly,
        isFinancial: !readOnly,
        supportDelegationEligible: false,
        delegationPolicy: 'NOT_ALLOWED',
        accessType: readOnly ? 'READ' : 'WRITE',
    };
}
const BASE_ORGANIZATION_GOVERNANCE_OPERATIONS = {
    [ORGANIZATION_GOVERNANCE_WORKSPACE_READ]: operation(ORGANIZATION_GOVERNANCE_WORKSPACE_READ, 'Access organization governance workspace', AREAS.workspace, { readOnly: true }),
    [ACCOUNTING_GOVERNANCE_READ]: operation(ACCOUNTING_GOVERNANCE_READ, 'Read accounting governance results', AREAS.accounting, { readOnly: true }),
    [ACCOUNTING_GOVERNANCE_RECORD]: operation(ACCOUNTING_GOVERNANCE_RECORD, 'Record accounting governance result', AREAS.accounting),
    [COMPLIANCE_GOVERNANCE_READ]: operation(COMPLIANCE_GOVERNANCE_READ, 'Read confidential compliance governance', AREAS.compliance, { readOnly: true }),
    [COMPLIANCE_GOVERNANCE_RECORD]: operation(COMPLIANCE_GOVERNANCE_RECORD, 'Record compliance governance evidence', AREAS.compliance),
    [COMPLIANCE_GOVERNANCE_APPROVE]: operation(COMPLIANCE_GOVERNANCE_APPROVE, 'Approve compliance governance decision', AREAS.compliance, { makerChecker: true }),
    [CLIENT_GOVERNANCE_READ]: operation(CLIENT_GOVERNANCE_READ, 'Read client governance records', AREAS.clients, { readOnly: true }),
    [CLIENT_GOVERNANCE_RECORD]: operation(CLIENT_GOVERNANCE_RECORD, 'Manage client governance lifecycle', AREAS.clients),
    [PRIVACY_RIGHTS_READ]: operation(PRIVACY_RIGHTS_READ, 'Read privacy-rights requests', AREAS.privacy, { readOnly: true }),
    [PRIVACY_RIGHTS_MANAGE]: operation(PRIVACY_RIGHTS_MANAGE, 'Manage privacy-rights requests', AREAS.privacy),
    [INTEGRATION_GOVERNANCE_READ]: operation(INTEGRATION_GOVERNANCE_READ, 'Read integration governance evidence', AREAS.integrations, { readOnly: true }),
    [INTEGRATION_GOVERNANCE_MANAGE]: operation(INTEGRATION_GOVERNANCE_MANAGE, 'Manage integration governance lifecycle', AREAS.integrations),
    [CONTROL_PLANE_ENTITLEMENTS_SYNCHRONIZE]: operation(CONTROL_PLANE_ENTITLEMENTS_SYNCHRONIZE, 'Synchronize signed control-plane entitlements', AREAS.integrations),
    [CONTROL_PLANE_REFERENCE_DATA_SYNCHRONIZE]: operation(CONTROL_PLANE_REFERENCE_DATA_SYNCHRONIZE, 'Synchronize signed reference-data release', AREAS.integrations),
    [CONTROL_PLANE_SUPPORT_DELEGATION_SYNCHRONIZE]: operation(CONTROL_PLANE_SUPPORT_DELEGATION_SYNCHRONIZE, 'Synchronize signed support delegation', AREAS.integrations),
    [MONETARY_APPROVAL_READ]: operation(MONETARY_APPROVAL_READ, 'Read monetary approval authorities', AREAS.monetary, { readOnly: true }),
    [MONETARY_APPROVAL_MANAGE]: operation(MONETARY_APPROVAL_MANAGE, 'Manage monetary approval authority', AREAS.monetary),
    [MONETARY_APPROVAL_DECIDE]: operation(MONETARY_APPROVAL_DECIDE, 'Record and commit monetary approval decision', AREAS.monetary, { makerChecker: true }),
    [RISK_ANALYTICS_READ]: operation(RISK_ANALYTICS_READ, 'Read risk analyses and breaches', AREAS.risk, { readOnly: true }),
    [RISK_ANALYTICS_RUN]: operation(RISK_ANALYTICS_RUN, 'Run immutable risk analysis', AREAS.risk),
    [RISK_ANALYTICS_RESOLVE]: operation(RISK_ANALYTICS_RESOLVE, 'Resolve risk limit breach', AREAS.risk, { makerChecker: true }),
};
export const ORGANIZATION_GOVERNANCE_OPERATIONS = {
    ...BASE_ORGANIZATION_GOVERNANCE_OPERATIONS,
    ...FEE_RULE_OPERATIONS,
    ...ORGANIZATION_REGULATORY_PROFILE_OPERATIONS,
};
export const ORGANIZATION_GOVERNANCE_OPERATION_IDS = Object.freeze([
    ...Object.keys(BASE_ORGANIZATION_GOVERNANCE_OPERATIONS),
    ...FEE_RULE_OPERATION_IDS,
    ...Object.keys(ORGANIZATION_REGULATORY_PROFILE_OPERATIONS),
]);
export * from './fee-rule.operations.js';
export * from './organization-regulatory-profile.operations.js';
